import React from "react";
import { Menu } from "lucide-react";

import { useDemoStatus } from "@/hooks/useDemoStatus";
import { cn } from "@/utils";

interface TopBarProps {
  onMobileMenuToggle: () => void;
}

/**
 * Slim dark topbar (Stripe Sandbox-style) — только бренд и env-метка.
 * Высота 52px. Logout живёт в client-card sidebar.
 * Sidebar collapse-toggle живёт на границе sidebar (см. SidebarToggle).
 */
export const TopBar: React.FC<TopBarProps> = ({ onMobileMenuToggle }) => {
  // Runtime-окружение приходит из build-arg VITE_ENVIRONMENT (см. Dockerfile.prod
  // и .gitlab-ci.yml). `MODE` нельзя использовать — он = "production" для любой
  // prod-сборки, включая dev-стенд.
  const rawEnv = (import.meta.env.VITE_ENVIRONMENT as string | undefined) || "production";
  // На боевом контуре метка ничего не сообщает — там работают все. Показываем
  // ее только на dev и прочих стендах, где важно не спутать контур.
  const env = rawEnv === "production" ? null : `${rawEnv.toUpperCase()} ENVIRONMENT`;
  const demo = useDemoStatus();

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-30",
        "h-[52px] bg-surface-topbar text-top-ink-1 border-b border-white/[0.06]",
      )}
    >
      <div className="h-full pl-4 pr-4 flex items-center gap-3">
        {/* Mobile menu toggle */}
        <button
          onClick={onMobileMenuToggle}
          className={cn(
            "lg:hidden -ml-2 p-2 rounded-pill",
            "text-top-ink-2 hover:text-top-ink-1 hover:bg-white/10 transition-colors",
          )}
          aria-label="Открыть меню"
        >
          <Menu size={18} />
        </button>

        {/* Brand */}
        {/* Словесный знак как на inforensic.pro: DIN Pro Cond капсом в brand-оранжевом */}
        <div className="flex items-baseline gap-2.5">
          <span className="font-display text-fs-lg font-bold uppercase tracking-[0.02em] text-accent">Контекст</span>
          <span className="hidden sm:inline font-mono text-fs-2xs uppercase tracking-[0.14em] text-top-ink-3">
            by Inforensic
          </span>
        </div>

        {/* Бейдж демо-режима — рядом с брендом, как Sandbox у Stripe */}
        {demo?.is_demo && (
          <span
            className={cn(
              "inline-flex items-center px-2 py-0.5 rounded",
              "bg-warning-400 text-black",
              "font-mono text-fs-2xs font-bold tracking-[0.1em] uppercase",
            )}
            title="Демо-режим: данные вымышлены"
          >
            Демо
          </span>
        )}

        <div className="flex-1" />

        {/* Метка окружения — только вне прода */}
        {env && (
          <span
            className={cn(
              "hidden sm:inline-flex items-center px-2 py-1 rounded",
              "border border-white/15 bg-white/[0.06]",
              "font-mono text-fs-2xs font-semibold tracking-[0.08em] text-top-ink-3 uppercase",
            )}
          >
            {env}
          </span>
        )}
      </div>
    </header>
  );
};

export default TopBar;
